<template>
  <div ref="menu" class="shadowed sticky-top" :class="[store.themeEnvironment]">
    <div class="content">
      <nav class="navbar navbar-expand-md">
        <!-- Mobile main menu toggle -->
        <button class="navbar-toggler collapsed" type="button" data-toggle="collapse" data-target="#collapsible-navbar">
          <span class="navbar-toggler-icon" />
        </button>

        <!-- Search -->
        <ProductSearchComponent
          class="search"
          allow-multi-line
          :data-culture="store.dataCulture"
          :included-extra-countries="store.includedCountries"
          include-categories
          @select="navigateToProduct($event.value)"
          @filter="filter($event)"
        />

        <!-- Data for menu -->
        <div class="cultures nav-item dropdown position-relative">
          <a
            id="dropdownMenuButton"
            class="nav-link dropdown-toggle d-flex align-items-center"
            type="button"
            data-toggle="dropdown"
            :title="
              appTranslate(
                'Filters products that aren\'t available in the selected countries. Displays cultural data for the selected primary country first hand',
              )
            "
          >
            <span class="data-for mr-1">{{ appTranslate('Data for') }}</span>

            <div class="d-flex gap-1 mr-1">
              <i v-if="store.productType === 'product' || !store.productType" :class="indexedProductTypeOptions.product?.icon" />
              <i v-if="store.productType === 'component' || !store.productType" :class="indexedProductTypeOptions.component?.icon" />
            </div>

            <img v-if="store.dataCulture" class="flag" :src="getFlag(store.dataCulture.country)" />
            <i v-else class="flag no-flag" />
          </a>
          <div class="selected-countries">
            <div class="position-absolute">
              <template v-for="country of store.includedCountries" :key="country">
                <img v-if="country !== store.dataCulture?.country" class="tiny-flag" :src="getFlag(country)" />
              </template>
              <i v-if="store.includeProductsMissingAvailableCountries && store.dataCulture" class="tiny-flag no-flag" />
            </div>
          </div>

          <div class="dropdown-menu dropdown-menu-right" @click.stop>
            <div class="px-2">
              <ToggleComponent
                v-model="store.productType"
                :options="productTypeOptions"
                class="flex-nowrap"
                style="justify-content: space-between"
                :colour="store.includeAllProductTypes ? 'grey' : 'theme'"
              />
            </div>

            <div class="dropdown-divider" />

            <div class="d-flex">
              <button class="dropdown-item" @click.stop="store.toggleAllCultures()">
                {{ appTranslate(`${store.allCulturesAreIncluded ? 'Deselect' : 'Select'} all`) }}
              </button>
            </div>

            <div class="dropdown-divider" />

            <div v-for="culture of store.availableCultures" :key="culture.country" class="d-flex">
              <button
                class="dropdown-item culture m-0"
                :class="[
                  {
                    active: store.dataCulture?.country === culture.country,
                    selected: store.explicitlySelectedCountries.has(culture.country),
                    included: store.includedCountries.has(culture.country),
                  },
                ]"
                :title="
                  appTranslate(store.explicitlySelectedCountries.has(culture.country) ? 'Exclude %s' : 'Include %s', [
                    translate(culture.country, 'countries', 'capitalize'),
                  ])
                "
                @click="store.dataCulture?.country === culture.country ? undefined : store.toggleCountry(culture.country)"
              >
                <!-- <input type="checkbox" class="d-none" :checked="store.selectedCountries.has(culture.country)"  /> -->
                <img class="flag" :src="getFlag(culture.country)" />
                {{ translate(culture.country, 'countries', 'capitalize') }}
                <small
                  v-if="productsStore.currentTotalProductsByCountry && router.currentRoute.value.name === 'products'"
                  class="ml-1"
                  style="vertical-align: center"
                >
                  ({{ productsStore.currentTotalProductsByCountry?.[culture.country] ?? 0 }})
                </small>
              </button>
              <button
                class="dropdown-item master-culture popup-trigger position-relative"
                :class="{ active: store.dataCulture?.country === culture.country }"
                :title="
                  appTranslate(
                    'Set as primary country. Displays cultural data from this country first hand and dictates culture used during export of cultural fields without prefix',
                  )
                "
                @click="store.setMainDataCulture(culture)"
              >
                <!-- <span class="description popup culture-popup">Set as primary country</span> -->

                <i class="fa fa-star text-white" />
              </button>
            </div>

            <div class="dropdown-divider" />

            <div class="d-flex">
              <button
                class="dropdown-item culture m-0"
                :class="{
                  active: !store.dataCulture,
                  selected: store.explicityIncludeProductsMissingAvailableCountries,
                  included: store.overrideIncludeAllCultures,
                }"
                style="border-right: none"
                :title="appTranslate('Include products that are missing country availability')"
                @click="store.toggleIncludeProductsMissingAvailableCountries()"
              >
                <i class="flag no-flag" />
                {{ appTranslate('Missing country') }}
                <small v-if="productsStore.currentTotalProductsByCountry" class="ml-2">
                  ({{ productsStore.currentTotalProductsByCountry?.missing ?? 0 }})
                </small>
              </button>
              <!-- <div class="master-culture"></div> -->
              <button
                class="dropdown-item master-culture popup-trigger position-relative"
                :class="{ active: !store.dataCulture?.country }"
                @click="store.setMainDataCulture(null)"
              >
                <i class="fa fa-star text-white" />
              </button>
            </div>
          </div>
        </div>

        <!-- Main menu items -->
        <div id="collapsible-navbar" class="collapse navbar-collapse navigation">
          <ul class="navbar-nav">
            <li class="nav-item">
              <router-link :to="{ path: '/', force: true, state: { forceUpdate: true } }" class="nav-link" active-class="underlined">
                <i class="fa fa-home" style="font-size: 24px" />
              </router-link>
            </li>

            <li v-if="shownAdminMenuRoutes.length" class="nav-item dropdown">
              <!-- Admin menu -->
              <a id="dropdownMenuButton" class="nav-link dropdown-toggle" type="button" data-toggle="dropdown">Admin</a>
              <div class="dropdown-menu">
                <router-link
                  v-for="(adminRoute, i) in shownAdminMenuRoutes"
                  :key="i"
                  class="dropdown-item"
                  active-class="active"
                  :to="{ name: adminRoute.name, force: !!adminRoute.meta?.alwaysReload, state: { forceUpdate: !!adminRoute.meta?.alwaysReload } }"
                >
                  {{ adminRoute.meta?.title }}
                </router-link>
              </div>
            </li>

            <li class="nav-item dropdown" title="Påverkar språk i applikationen samt rubriker och värden på fält">
              <a id="dropdownMenuButton" class="nav-link dropdown-toggle" type="button" data-toggle="dropdown">
                {{ appTranslate('Language') }}:
                {{ translate(store.translationLanguage, 'languages', undefined, store.translationLanguage) }}
              </a>
              <div class="dropdown-menu">
                <button
                  v-for="language of store.availableLanguages"
                  :key="language"
                  class="link dropdown-item"
                  :class="{ active: store.translationLanguage === language }"
                  @click="store.setTranslationLanguage(language)"
                >
                  {{ translate(language, 'languages', undefined, store.translationLanguage) }}
                </button>
              </div>
            </li>

            <!-- User menu -->
            <li class="nav-item dropdown">
              <a class="nav-link dropdown-toggle" type="button" data-toggle="dropdown">
                <i class="fa fa-wrench mr-1" />
                <i class="fa fa-user-circle" />
              </a>
              <div class="dropdown-menu dropdown-menu-right">
                <router-link to="/image-search" class="dropdown-item" active-class="active"> {{ appTranslate('Image search') }} </router-link>
                <router-link v-if="store.userPrivileges.has('export')" to="/ean-label-tester" class="dropdown-item" active-class="active">
                  EAN-tester
                </router-link>
                <router-link
                  v-if="!shownAdminMenuRoutes.some((link) => link.name === 'presentations')"
                  to="/presentations"
                  class="dropdown-item"
                  active-class="active"
                >
                  {{ appTranslate('Presentations') }}
                </router-link>

                <div class="dropdown-divider" />
                <a class="dropdown-item" :class="{ active: store.showDiscreetAvailabilityStatuses }" @click.stop="store.toggleDiscreetAvailabilityStatuses()">
                  Visa diskret artikelstatus
                </a>

                <div class="dropdown-divider" />

                <a
                  v-if="store.userPrivileges?.has('accessToDevTools')"
                  class="dropdown-item"
                  style="font-size: 14px"
                  @click="DataService.clearFilterMatchCache()"
                >
                  Rensa filter match cache
                  <small v-if="store.settings?.app.filterCacheExpirationMinutes" class="d-block">
                    (maxtid {{ store.settings?.app.filterCacheExpirationMinutes }} min)
                  </small>
                </a>
                <!-- <a
                  v-if="store.userPrivileges?.has('accessToDevTools')"
                  class="dropdown-item"
                  style="font-size: 14px"
                  @click="DataService.clearSimplifiedProductsCache()"
                >
                  Rensa simplified artikelcache
                  <small v-if="store.settings?.app.simplifiedProductsCacheExpirationMinutes" class="d-block">
                    (maxtid {{ store.settings?.app.simplifiedProductsCacheExpirationMinutes }} min)
                  </small>
                </a> -->
                <a
                  v-if="store.userPrivileges?.has('accessToDevTools')"
                  class="dropdown-item"
                  style="font-size: 14px"
                  @click="DataService.clearProductImgCache()"
                >
                  Rensa bildcache
                  <small v-if="store.settings?.app.imgCacheExpirationMinutes" class="d-block">
                    (maxtid {{ store.settings?.app.imgCacheExpirationMinutes }} min)
                  </small>
                </a>
                <button class="dropdown-item" @click="AuthService.signOutWithMessage()">
                  {{ appTranslate('Sign out') }}
                  <br />
                  <small style="font-size: 10px">{{ store.currentUser?.email }}</small>
                </button>
              </div>
            </li>
          </ul>
        </div>
      </nav>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { useTools } from '@/composables/useTools'
import { useTranslation } from '@/composables/useTranslation'
import { PartialRecord } from '@/models/GenericInterfaces'
import { Option } from '@/models/Global'
import Message from '@/models/Message'
import { ProductType, SimplifiedProduct } from '@/models/Product'
import { adminRoutesInTopMenu } from '@/router'
import AuthService from '@/services/AuthService'
import DataService from '@/services/DataService'
import { useDefaultStore } from '@/store'
import { useProductsStore } from '@/store/productsArchiveStore'
import { Debouncer } from '@/utils/Debouncer'
import { computed, onMounted, ref } from 'vue'
import { useRouter } from 'vue-router'
import ProductSearchComponent from './products/ProductSearchComponent.vue'
import ToggleComponent from './ToggleComponent.vue'
import { getProductTypeIconClass } from '@/utils/tools.js'

// -----------
// -- Setup --
// -----------
const { translate, appTranslate } = useTranslation()
const { getFlag } = useTools()
const store = useDefaultStore()
const productsStore = useProductsStore()
const router = useRouter()

const resizeDebouncer = new Debouncer()
const resizeObserver = new ResizeObserver((entries) => {
  resizeDebouncer.debounce(() => {
    for (const entry of entries) {
      store.topMenuHeight = entry.target.clientHeight
    }
  }, 50)
})

// ---------------
// -- Reactives --
// ---------------
const menu = ref<HTMLElement>()

const shownAdminMenuRoutes = computed(() =>
  adminRoutesInTopMenu.filter(
    (item) =>
      item.meta?.requiresPrivilege?.some((privilege) => store.userPrivileges?.has(privilege)) ||
      (item.meta?.showInAdminMenu && typeof item.meta?.showInAdminMenu !== 'boolean' && store.userPrivileges?.has(item.meta?.showInAdminMenu)) ||
      (item.meta?.requiresSuperAdmin && store.currentUser?.role === 'superAdmin'),
  ),
)

const productTypeOptions = computed((): Option<ProductType | undefined>[] => [
  { value: 'product', displayValue: appTranslate('Products'), icon: getProductTypeIconClass('product') },
  { value: 'component', displayValue: appTranslate('Components'), icon: getProductTypeIconClass('component') },
  {
    value: undefined,
    displayValue: appTranslate('Both'),
    icon: [getProductTypeIconClass('product'), getProductTypeIconClass('component')],
  },
])

const indexedProductTypeOptions = computed(() =>
  productTypeOptions.value.reduce<PartialRecord<ProductType, Option<ProductType | undefined>>>((acc, option) => {
    if (option.value) {
      acc[option.value] = option
    }
    return acc
  }, {}),
)

// ---------------------
// -- Lifecycle hooks --
// ---------------------
onMounted(() => {
  if (menu.value) {
    resizeObserver.observe(menu.value)
  }
})

// ---------------
// -- Functions --
// ---------------

const addTestMsg = (index: number) => {
  const msgs = [
    new Message('test', 'testar', 'danger'),
    new Message('Bara titel som är i och för sig lite längre än alla de andra'),
    new Message(
      'Test',
      'Testar med ett långt meddelande. Testar med ett långt meddelande. Testar med ett långt meddelande. Testar med ett långt meddelande. Testar med ett långt meddelande. Testar med ett långt meddelande. Testar med ett långt meddelande. Testar med ett långt meddelande. Testar med ett långt meddelande.',
      'warning',
    ),
  ]
  store.addMessage(msgs[index])
}

const navigateToProduct = (product: SimplifiedProduct) => {
  router.push({ name: 'product', state: { modal: true }, params: { sku: product.sku } })
}

const filter = (query: { _q?: string; _categories?: string }) => {
  router.push({ name: 'products', query })
}
</script>

<style lang="scss" scoped>
@use '@/styles/popup.scss';

.shadowed {
  box-shadow: 0 10px 10px -10px var(--light-grey);
}

.content {
  background-color: var(--theme-bg);
  text-shadow: none;

  .navbar-toggler-icon,
  a.nav-link {
    color: var(--theme-link-colour);
  }
}

nav {
  margin-left: 0 !important;

  width: 100%;
  // max-width: 1500px !important;
  // max-width: 100vw !important;
  max-width: calc(100vw - 17px) !important;

  flex-wrap: wrap !important;
  user-select: none;
  -webkit-user-drag: none;
  font-size: 0.9em !important;
  z-index: 100;

  .flag {
    --flag-width: 30px;
  }

  @media (min-width: 576px) {
    nav {
      max-width: calc(100vw - 38px) !important;
    }
  }

  @media (max-width: 767px) {
    padding-left: 0;
    padding-right: 0;

    .cultures .nav-link {
      padding: 0 0.5rem;
    }

    .navbar-collapse {
      order: 10;
    }

    .data-for {
      display: none;
    }

    ul {
      align-items: start;
      // align-items: ;
      // text-align: center;
      // justify-content: flex-start;
    }
  }

  .navigation {
    margin-right: 8%;
  }

  ul.navbar-nav {
    display: flex;
    width: 100%;
    justify-content: end; //center;

    li {
      padding: 0 1.5em;
    }

    @media (max-width: 992px) {
      margin-right: 0;

      li {
        padding: 0 1em;
      }
    }
  }

  .navbar-toggler-icon {
    background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' width='30' height='30' viewBox='0 0 30 30'%3e%3cpath stroke='rgba%280, 0, 0, 0.5%29' stroke-linecap='round' stroke-miterlimit='10' stroke-width='2' d='M4 7h22M4 15h22M4 23h22'/%3e%3c/svg%3e");
  }

  .navbar-brand {
    .logo {
      height: 4em;
    }
  }

  .search {
    white-space: nowrap;
    flex: 1 1 8rem;
    max-width: 24rem;
  }
}

.dropdown {
  user-select: none;
  position: relative;
  display: inline-block;

  .dropdown-menu {
    min-width: fit-content;

    button.dropdown-item {
      outline: none;
    }
  }
}

.included {
  background-color: #ececec;
}

.selected {
  background-color: var(--theme-selected-secondary);
}

.dropdown-item.active,
.dropdown-item:active {
  background-color: var(--theme-selected-primary);
  // cursor: not-allowed;
  cursor: default;
}

// :deep {
//   input.form-control:focus {
//     border-color: var(--theme-bg);
//     box-shadow: 0 0 0 0.2rem var(--theme-bg);
//   }
// }
// }

.culture {
  border-right: 1px solid #eee;
  display: inline-flex;
  align-items: center;
  // flex: 0 0 210px;
  // width: 200px;

  > i,
  img {
    margin-right: 0.25rem;
  }
}

.master-culture {
  $width: 45px;
  text-align: center;
  width: $width;
  // flex-basis: $width;
  padding: 0;

  &:not(.active):hover .fa-star {
    -webkit-text-stroke-color: #aaa;
    -webkit-text-stroke-width: 1px;
  }

  position: relative;

  .culture-popup {
    position: fixed;
    transform: translate(calc($width + 2px));
  }
}

.selected-countries {
  display: flex;

  > div {
    display: flex;
    gap: 1px;
    line-height: 0;
    margin-top: -9px;
    margin-left: 1em;
    margin-right: 1em;
  }

  @media (max-width: 767px) {
    justify-content: flex-end;

    > div {
      margin-top: 11px;
    }
  }
}
</style>
